/**
 * color-math.ts — Pure OKLCH color math helpers.
 *
 * No React, no external deps. Implements the sRGB → linear → XYZ D65 → OKLab →
 * OKLCH pipeline from the W3C CSS Color 4 spec, sufficient for building a
 * theme delta engine from a single anchor color.
 *
 * References:
 *  - https://www.w3.org/TR/css-color-4/#color-conversion-code
 *  - https://bottosson.github.io/posts/oklab/
 */

export interface OKLCH {
  l: number // [0, 1]
  c: number // [0, 0.4]
  h: number // [0, 360) in degrees
  a: number // [0, 1] alpha
}

export interface Delta {
  dL: number
  dC: number
  dH: number
}

// ---------------------------------------------------------------------------
// Internal: sRGB ↔ linear ↔ XYZ D65 ↔ OKLab ↔ OKLCH
// ---------------------------------------------------------------------------

function linearize(v: number): number {
  const abs = Math.abs(v)
  if (abs <= 0.04045) return v / 12.92
  return Math.sign(v) * Math.pow((abs + 0.055) / 1.055, 2.4)
}

function srgbToLinear(r: number, g: number, b: number): [number, number, number] {
  return [linearize(r), linearize(g), linearize(b)]
}

function linearToXyzD65(r: number, g: number, b: number): [number, number, number] {
  // sRGB D65 matrix from the CSS Color 4 spec
  const x = 0.4123907992659595 * r + 0.357584339383878 * g + 0.1804807884018343 * b
  const y = 0.21263900587151027 * r + 0.715168678767756 * g + 0.07219231536073371 * b
  const z = 0.01933081871559182 * r + 0.11919477979462598 * g + 0.9505321522496608 * b
  return [x, y, z]
}

function xyzD65ToOklab(x: number, y: number, z: number): [number, number, number] {
  // From Björn Ottosson's OKLab post, exact matrix values
  const l = 0.8189330101 * x + 0.3618667424 * y - 0.1288597137 * z
  const m = 0.0329845436 * x + 0.9293118715 * y + 0.0361456387 * z
  const s = 0.0482003018 * x + 0.2643662691 * y + 0.6338517070 * z

  const l_ = Math.cbrt(l)
  const m_ = Math.cbrt(m)
  const s_ = Math.cbrt(s)

  const L = 0.2104542553 * l_ + 0.7936177850 * m_ - 0.0040720468 * s_
  const a = 1.9779984951 * l_ - 2.4285922050 * m_ + 0.4505937099 * s_
  const B = 0.0259040371 * l_ + 0.7827717662 * m_ - 0.8086757660 * s_
  return [L, a, B]
}

function oklabToOklch(L: number, a: number, b: number): [number, number, number] {
  const c = Math.sqrt(a * a + b * b)
  let h = (Math.atan2(b, a) * 180) / Math.PI
  if (h < 0) h += 360
  return [L, c, h]
}

function oklchToOklab(l: number, c: number, h: number): [number, number, number] {
  const hRad = (h * Math.PI) / 180
  return [l, c * Math.cos(hRad), c * Math.sin(hRad)]
}

function oklabToXyzD65(L: number, a: number, b: number): [number, number, number] {
  const l_ = L + 0.3963377774 * a + 0.2158037573 * b
  const m_ = L - 0.1055613458 * a - 0.0638541728 * b
  const s_ = L - 0.0894841775 * a - 1.2914855480 * b

  const l = l_ * l_ * l_
  const m = m_ * m_ * m_
  const s = s_ * s_ * s_

  const x = 1.2270138511 * l - 0.5577999807 * m + 0.2812561490 * s
  const y = -0.0405801784 * l + 1.1122568696 * m - 0.0716766787 * s
  const z = -0.0763812845 * l - 0.4214819784 * m + 1.5861632204 * s
  return [x, y, z]
}

function xyzD65ToLinear(x: number, y: number, z: number): [number, number, number] {
  // Inverse of linearToXyzD65 (sRGB D65 inverse matrix)
  const r = 3.2409699419045226 * x - 1.5373831775700939 * y - 0.4986107602930034 * z
  const g = -0.9692436362808796 * x + 1.8759675015077202 * y + 0.0415550574071756 * z
  const b = 0.0556300796969936 * x - 0.2039769588889765 * y + 1.0569715142428786 * z
  return [r, g, b]
}

function delinearize(v: number): number {
  const abs = Math.abs(v)
  if (abs <= 0.0031308) return v * 12.92
  return Math.sign(v) * (1.055 * Math.pow(abs, 1 / 2.4) - 0.055)
}

/** Convert OKLCH → sRGB [0,1] (may produce out-of-gamut values; callers clamp). */
function oklchToSrgb(l: number, c: number, h: number): [number, number, number] {
  const [La, a, b] = oklchToOklab(l, c, h)
  const [x, y, z] = oklabToXyzD65(La, a, b)
  const [lr, lg, lb] = xyzD65ToLinear(x, y, z)
  return [delinearize(lr), delinearize(lg), delinearize(lb)]
}

/** Convert sRGB [0,1] → OKLCH. */
function srgbToOklch(r: number, g: number, b: number): OKLCH {
  const [lr, lg, lb] = srgbToLinear(r, g, b)
  const [x, y, z] = linearToXyzD65(lr, lg, lb)
  const [L, a, Bv] = xyzD65ToOklab(x, y, z)
  const [ol, oc, oh] = oklabToOklch(L, a, Bv)
  return { l: ol, c: oc, h: oh, a: 1 }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Parse any common CSS color string into OKLCH.
 * Supports: #rrggbb, #rgb, rgb(), rgba(), hsl(), hsla(), oklch().
 * Returns null for unknown / unparseable inputs.
 */
export function parseCssColor(input: string): OKLCH | null {
  const s = input.trim().toLowerCase()
  if (!s || s === 'transparent' || s === 'none') return null

  // -- hex --
  const hex6 = /^#([0-9a-f]{6})$/.exec(s)
  if (hex6) {
    // Group 1 is guaranteed present when the regex matches.
    const n = parseInt(hex6[1] ?? '000000', 16)
    const r = ((n >> 16) & 0xff) / 255
    const g = ((n >> 8) & 0xff) / 255
    const b = (n & 0xff) / 255
    return { ...srgbToOklch(r, g, b), a: 1 }
  }
  const hex3 = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/.exec(s)
  if (hex3) {
    // Groups 1–3 are guaranteed present when the regex matches.
    const r = parseInt(`${hex3[1] ?? '0'}${hex3[1] ?? '0'}`, 16) / 255
    const g = parseInt(`${hex3[2] ?? '0'}${hex3[2] ?? '0'}`, 16) / 255
    const b = parseInt(`${hex3[3] ?? '0'}${hex3[3] ?? '0'}`, 16) / 255
    return { ...srgbToOklch(r, g, b), a: 1 }
  }

  // -- rgb / rgba --
  const rgbMatch = /^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)(?:\s*,\s*([\d.]+))?\s*\)$/.exec(s)
  if (rgbMatch) {
    // Groups 1–3 are guaranteed to be present when the regex matched.
    const r = parseFloat(rgbMatch[1] ?? '0') / 255
    const g = parseFloat(rgbMatch[2] ?? '0') / 255
    const b = parseFloat(rgbMatch[3] ?? '0') / 255
    const alpha = rgbMatch[4] != null ? parseFloat(rgbMatch[4]) : 1
    return { ...srgbToOklch(r, g, b), a: alpha }
  }

  // -- hsl / hsla --
  const hslMatch = /^hsla?\(\s*([\d.]+)\s*,\s*([\d.]+)%\s*,\s*([\d.]+)%(?:\s*,\s*([\d.]+))?\s*\)$/.exec(s)
  if (hslMatch) {
    // Groups 1–3 are guaranteed to be present when the regex matched.
    const h = parseFloat(hslMatch[1] ?? '0')
    const sat = parseFloat(hslMatch[2] ?? '0') / 100
    const light = parseFloat(hslMatch[3] ?? '0') / 100
    const alpha = hslMatch[4] != null ? parseFloat(hslMatch[4]) : 1
    const [r, g, b] = hslToRgb(h, sat, light)
    return { ...srgbToOklch(r, g, b), a: alpha }
  }

  // -- oklch --
  // Handles: oklch(0.72 0.18 25) oklch(72% 0.18 25) oklch(0.72 0.18 25 / 0.5)
  const oklchMatch = /^oklch\(\s*([\d.]+%?)\s+([\d.]+)\s+([\d.]+)(?:\s*\/\s*([\d.]+%?))?\s*\)$/.exec(s)
  if (oklchMatch) {
    // Groups 1–3 are guaranteed to be present when the regex matched.
    const rawL = oklchMatch[1] ?? '0'
    let l = parseFloat(rawL)
    if (rawL.endsWith('%')) l /= 100
    const c = parseFloat(oklchMatch[2] ?? '0')
    const h = parseFloat(oklchMatch[3] ?? '0')
    let alpha = 1
    const rawA = oklchMatch[4]
    if (rawA != null) {
      alpha = rawA.endsWith('%') ? parseFloat(rawA) / 100 : parseFloat(rawA)
    }
    return { l, c, h, a: alpha }
  }

  return null
}

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  const c = (1 - Math.abs(2 * l - 1)) * s
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
  const m = l - c / 2
  let r = 0, g = 0, b = 0
  if (h < 60) { r = c; g = x }
  else if (h < 120) { r = x; g = c }
  else if (h < 180) { g = c; b = x }
  else if (h < 240) { g = x; b = c }
  else if (h < 300) { r = x; b = c }
  else { r = c; b = x }
  return [r + m, g + m, b + m]
}

/**
 * Emit `oklch(L% C H / a)` from an OKLCH object.
 * Lightness in percent, chroma to 4 decimals, hue to 2 decimals.
 */
export function formatOklch({ l, c, h, a }: OKLCH): string {
  const lPct = (l * 100).toFixed(2)
  const cStr = c.toFixed(4)
  const hStr = h.toFixed(2)
  if (Math.abs(a - 1) < 0.0001) {
    return `oklch(${lPct}% ${cStr} ${hStr})`
  }
  return `oklch(${lPct}% ${cStr} ${hStr} / ${a.toFixed(3)})`
}

/** Compute the OKLCH delta: target − current. */
export function shiftDelta(current: OKLCH, target: OKLCH): Delta {
  // Hue delta: take the shortest arc (−180..+180)
  let dH = target.h - current.h
  if (dH > 180) dH -= 360
  if (dH < -180) dH += 360
  return {
    dL: target.l - current.l,
    dC: target.c - current.c,
    dH,
  }
}

/** Apply a delta to a base OKLCH. Clamps l/c, wraps h. Preserves alpha. */
export function applyDelta(base: OKLCH, d: Delta): OKLCH {
  const l = Math.max(0, Math.min(1, base.l + d.dL))
  const c = Math.max(0, Math.min(0.4, base.c + d.dC))
  let h = (base.h + d.dH) % 360
  if (h < 0) h += 360
  return { l, c, h, a: base.a }
}

/**
 * WCAG 2.1 relative luminance from an OKLCH color (converts to sRGB first).
 * Returns a value in [0, 1].
 */
function relativeLuminance(ok: OKLCH): number {
  // Convert to sRGB and clamp to gamut
  const [r, g, b] = oklchToSrgb(ok.l, ok.c, ok.h)
  const rc = Math.max(0, Math.min(1, r))
  const gc = Math.max(0, Math.min(1, g))
  const bc = Math.max(0, Math.min(1, b))
  // WCAG formula uses linearized values
  const [lr, lg, lb] = srgbToLinear(rc, gc, bc)
  return 0.2126 * lr + 0.7152 * lg + 0.0722 * lb
}

/**
 * Convert any parseable CSS color (oklch / rgb / hsl / hex) into a
 * `#rrggbb` string. Used to feed third-party libs (Mermaid, Recharts) that
 * predate the `oklch()` syntax — calling `getComputedStyle` alone isn't
 * enough since Chromium ≥ 111 returns `oklch()` verbatim from a probe
 * element. Returns `fallback` when the input can't be parsed.
 *
 * Sentry CLUBMANAGER-PWA-15: mermaid choking on raw oklch.
 */
export function colorToHex(input: string, fallback: string): string {
  const ok = parseCssColor(input)
  if (!ok) return fallback
  const [r, g, b] = oklchToSrgb(ok.l, ok.c, ok.h)
  const to255 = (v: number) =>
    Math.max(0, Math.min(255, Math.round(v * 255)))
  const hex = (v: number) => to255(v).toString(16).padStart(2, '0')
  return `#${hex(r)}${hex(g)}${hex(b)}`
}

/**
 * WCAG 2.1 contrast ratio between two OKLCH colors.
 * Returns a value in [1, 21]; ≥ 4.5 passes AA for normal text.
 */
export function contrastRatio(fg: OKLCH, bg: OKLCH): number {
  const l1 = relativeLuminance(fg)
  const l2 = relativeLuminance(bg)
  const lighter = Math.max(l1, l2)
  const darker = Math.min(l1, l2)
  return (lighter + 0.05) / (darker + 0.05)
}
